import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, sheetRasterUrl } from "@/lib/api";
import { computeDiffRegions } from "@/lib/diffRegions";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/utils";
import RevisionCloud, { CLOUD_PALETTE } from "@/components/RevisionCloud";
import { AckPill, AckSummary } from "@/components/AckPill";
import {
  ArrowLeft, Columns, Layers3, Zap, Loader2, Share2, FileCheck, ChevronDown,
  Check, X, ChevronRight,
} from "lucide-react";

/**
 * Revision Review · Super
 *
 * Operationalizes the iter-24/26 diff engine into a real superintendent /
 * PM review workflow. The drawing remains the hero; the right-side panel
 * is just a navigable index of changes already painted on the drawing.
 *
 * Workflow:
 *   1. Open from REV badge on Sheet Viewer.
 *   2. Header establishes review mode + reviewer identity + Rev pair.
 *   3. Diff overlay paints cloud-shaped Added / Removed / Modified regions
 *      directly on the drawing (the only place an engineer trusts).
 *   4. Right-side categorized change list lets the reviewer walk every
 *      change. Click → smooth camera transition focuses that region.
 *   5. Optional summary text + Submit Review → permanent audit record.
 *   6. Share Review → copy a URL containing the review's share token.
 *
 * NOT comments, NOT chat, NOT kanban — just operational acknowledgment.
 */
export default function RevisionCompare() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sheet, setSheet] = useState(null);
  const [project, setProject] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [other, setOther] = useState(null);

  const [mode, setMode] = useState("overlay"); // 'side' | 'overlay' | 'blink'
  const [opacity, setOpacity] = useState(0.5);
  const [diffOn, setDiffOn] = useState(true);   // ON by default in review mode
  const [diffSensitivity, setDiffSensitivity] = useState(0.5);
  const [diffRegions, setDiffRegions] = useState({ regions: [], dims: { w: 0, h: 0 } });
  const [diffLoading, setDiffLoading] = useState(false);
  const [blinkShowOther, setBlinkShowOther] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(null);   // global region index that's currently focused
  const [showSummary, setShowSummary] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [decision, setDecision] = useState("carry_forward_approved");
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [shareToast, setShareToast] = useState(null);
  const [ackSummary, setAckSummary] = useState(null);

  // Load sheet + revisions + project + existing reviews.
  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    api.get(`/sheets/${sheetId}`).then((r) => {
      if (cancelled) return;
      setSheet(r.data);
      if (r.data?.project_id) {
        api.get(`/projects/${r.data.project_id}`).then((p) => !cancelled && setProject(p.data)).catch(() => {});
      }
    });
    api.get(`/sheets/${sheetId}/revisions`).then((r) => {
      if (cancelled) return;
      setRevisions(r.data || []);
      const otherRev = (r.data || []).find((rv) => rv.id !== sheetId);
      if (otherRev) setOther(otherRev);
    });
    api.get(`/sheets/${sheetId}/reviews`).then((r) => !cancelled && setReviews(r.data || [])).catch(() => {});
    api.get(`/sheets/${sheetId}/ack_summary`).then((r) => !cancelled && setAckSummary(r.data || null)).catch(() => {});
    return () => { cancelled = true; };
  }, [sheetId]);

  // Blink mode timer.
  useEffect(() => {
    if (mode !== "blink") { setBlinkShowOther(false); return; }
    const id = setInterval(() => setBlinkShowOther((v) => !v), 700);
    return () => clearInterval(id);
  }, [mode]);

  const otherUrl = other ? sheetRasterUrl(other.id, { dpr: 1 }) : null;
  const currentUrl = sheet ? sheetRasterUrl(sheet.id, { dpr: 1 }) : null;

  useEffect(() => {
    if (!diffOn || !otherUrl || !currentUrl) {
      setDiffRegions({ regions: [], dims: { w: 0, h: 0 } });
      return;
    }
    const ctl = new AbortController();
    setDiffLoading(true);
    const handle = setTimeout(async () => {
      try {
        const out = await computeDiffRegions(otherUrl, currentUrl, {
          sensitivity: diffSensitivity,
          signal: ctl.signal,
        });
        if (!ctl.signal.aborted) setDiffRegions(out);
      } catch { /* swallow */ }
      finally { if (!ctl.signal.aborted) setDiffLoading(false); }
    }, 220);
    return () => { ctl.abort(); clearTimeout(handle); setDiffLoading(false); };
  }, [diffOn, otherUrl, currentUrl, diffSensitivity]);

  // Group regions by category for the right-side review panel. Stable
  // numbering: regions are sorted by area-desc inside the engine; we preserve
  // that order across category groups, with each region carrying a global
  // 1-based index so the cloud chip + panel item agree.
  const grouped = useMemo(() => {
    const numbered = (diffRegions.regions || []).map((r, i) => ({ ...r, index: i + 1 }));
    return {
      all: numbered,
      added:    numbered.filter((r) => r.type === "added"),
      modified: numbered.filter((r) => r.type === "modified"),
      removed:  numbered.filter((r) => r.type === "removed"),
    };
  }, [diffRegions.regions]);

  const counts = {
    added: grouped.added.length,
    modified: grouped.modified.length,
    removed: grouped.removed.length,
    unchanged: 0,
  };

  // Camera focus — when a region is selected from the right panel, smooth-
  // scroll the stage so the region is roughly centred. We avoid zooming
  // (which would disorient the user); the operational rule is preserve
  // spatial continuity.
  const stageRef = useRef(null);
  const focusRegion = useCallback((region) => {
    if (!region || !stageRef.current) return;
    setFocusedIdx(region.index);
    const stage = stageRef.current;
    // Region centre in % of inner-content dims.
    const cx = region.nx + region.nw / 2;
    const cy = region.ny + region.nh / 2;
    // The inner content scrollable size:
    const content = stage.querySelector("[data-content]");
    if (!content) return;
    const cw = content.clientWidth;
    const ch = content.clientHeight;
    const targetLeft = Math.max(0, cx * cw - stage.clientWidth / 2);
    const targetTop  = Math.max(0, cy * ch - stage.clientHeight / 2);
    stage.scrollTo({ left: targetLeft, top: targetTop, behavior: "smooth" });
    // Clear focused style after a short window so the pulse doesn't linger.
    setTimeout(() => setFocusedIdx((cur) => (cur === region.index ? null : cur)), 1800);
  }, []);

  const submitReview = async () => {
    if (!other) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/sheets/${sheetId}/reviews`, {
        compared_with_id: other.id,
        decision,
        summary: summaryText.trim() || null,
        counts,
      });
      setReviews((rs) => [r.data, ...rs]);
      setShowSummary(false);
      setSummaryText("");
      setShareToast({ kind: "submitted", at: Date.now() });
      setTimeout(() => setShareToast(null), 2200);
    } catch (e) {
      setShareToast({ kind: "error", text: e?.response?.data?.detail || "Submit failed", at: Date.now() });
      setTimeout(() => setShareToast(null), 3200);
    } finally {
      setSubmitting(false);
    }
  };

  const shareReview = async () => {
    // Latest review for this sheet, if any. Otherwise share the compare URL.
    const latest = reviews[0];
    const url = latest
      ? `${window.location.origin}/sheet/${sheetId}/compare?token=${latest.share_token}`
      : `${window.location.origin}/sheet/${sheetId}/compare`;
    try {
      await navigator.clipboard.writeText(url);
      setShareToast({ kind: "share", text: "Review link copied", at: Date.now() });
    } catch {
      setShareToast({ kind: "share", text: url, at: Date.now() });
    }
    setTimeout(() => setShareToast(null), 2200);
  };

  if (!sheet) {
    return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]">Loading…</div>;
  }

  const onlyOne = revisions.length <= 1;
  const reviewerLabel = `Revision Review · ${ROLE_LABEL[user?.role] || "Reviewer"}`;
  const totalChanges = counts.added + counts.modified + counts.removed;

  return (
    <div className="flex flex-col h-screen bg-[#0a0a0b]" data-testid="revision-compare" data-compare-viewport="1">
      {/* Row 1 — operational identity header */}
      <div className="border-b border-[var(--fop-border)] bg-[var(--fop-surface)] px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="review-header">
        <Link
          to={`/sheet/${sheetId}`}
          className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] transition-colors"
          data-testid="compare-back"
          aria-label="Back to sheet"
        >
          <ArrowLeft size={16} />
        </Link>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            {project && (
              <>
                <span className="truncate max-w-[280px]" title={project.name}>{project.name}</span>
                <span className="text-[var(--fop-text-3)]/40">›</span>
              </>
            )}
            <span className="text-[var(--fop-primary)]" data-testid="review-mode-label">{reviewerLabel}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="font-mono text-sm font-bold text-[var(--fop-text)]">{sheet.sheet_number}</span>
            <span className="text-[12px] text-[var(--fop-text-2)] truncate max-w-[420px]">{sheet.sheet_title}</span>
          </div>
        </div>

        {/* Rev A vs Rev B selector — central operational pivot */}
        {!onlyOne && (
          <div className="flex items-center gap-2 ml-3 px-3 py-1 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border)]">
            <RevisionDropdown
              label="Current"
              value={sheet}
              options={revisions}
              onChange={(r) => navigate(`/sheet/${r.id}/compare`)}
              accent="var(--fop-primary)"
              testid="review-current-picker"
            />
            <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">vs</span>
            <RevisionDropdown
              label="Prior"
              value={other}
              options={revisions.filter((r) => r.id !== sheetId)}
              onChange={setOther}
              accent="var(--fop-text-3)"
              testid="review-prior-picker"
            />
          </div>
        )}

        <div className="ml-auto flex items-center gap-1">
          <HeaderBtn
            onClick={() => setShowSummary((v) => !v)}
            icon={FileCheck}
            label={showSummary ? "Cancel" : "Summary"}
            active={showSummary}
            testid="review-summary-btn"
          />
          <HeaderBtn
            onClick={shareReview}
            icon={Share2}
            label="Share"
            testid="review-share-btn"
          />
          {shareToast && (
            <div className={`ml-2 text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border ${
              shareToast.kind === "error"
                ? "border-red-500/40 text-red-300 bg-red-500/10"
                : "border-[var(--fop-primary)]/45 text-[var(--fop-primary)] bg-[var(--fop-primary-soft)]"
            }`} data-testid="review-share-toast">
              {shareToast.kind === "submitted" ? "Review recorded" : shareToast.text || "Link copied"}
            </div>
          )}
        </div>
      </div>

      {/* Row 2 — mode + diff sensitivity. Diff is always on in review mode. */}
      {!onlyOne && (
        <div className="border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)] px-4 py-1.5 flex items-center gap-2 flex-wrap">
          <ModeBtn active={mode === "overlay"} onClick={() => setMode("overlay")} icon={Layers3} label="Overlay" testid="compare-mode-overlay" />
          <ModeBtn active={mode === "side"} onClick={() => setMode("side")} icon={Columns} label="Side-by-side" testid="compare-mode-side" />
          <ModeBtn active={mode === "blink"} onClick={() => setMode("blink")} icon={Zap} label="Blink" testid="compare-mode-blink" />
          <div className="divider-v mx-2" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
            Sensitivity
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={diffSensitivity}
            onChange={(e) => setDiffSensitivity(parseFloat(e.target.value))}
            className="w-32 accent-[var(--fop-primary)]"
            data-testid="compare-diff-sensitivity"
          />
          {diffLoading && (
            <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
              <Loader2 size={11} className="animate-spin" />
              Computing
            </span>
          )}
          {/* Legend strip — engineering language for the overlay colours. */}
          <div className="flex items-center gap-3 ml-auto" data-testid="review-legend">
            <LegendChip kind="added" count={counts.added} />
            <LegendChip kind="modified" count={counts.modified} />
            <LegendChip kind="removed" count={counts.removed} />
            <button
              onClick={() => setDiffOn((v) => !v)}
              data-testid="compare-diff-toggle"
              className={`px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider border ${
                diffOn
                  ? "border-[var(--fop-primary)]/50 bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
                  : "border-[var(--fop-border)] text-[var(--fop-text-3)]"
              }`}
              title="Toggle revision overlay"
            >
              Diff · {diffOn ? "ON" : "OFF"}
              {diffOn && totalChanges > 0 && <span className="ml-1">· {totalChanges}</span>}
            </button>
          </div>
        </div>
      )}

      {onlyOne ? (
        <div className="p-10 text-center text-[var(--fop-text-2)]">
          <div className="font-display text-lg text-[var(--fop-text)] mb-1">No prior revision yet</div>
          <div className="text-sm text-[var(--fop-text-3)] max-w-md mx-auto">
            This sheet only has one revision on file. When a new revision is uploaded,
            you'll be able to review it here.
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-[1fr_320px]">
          {/* Stage */}
          <div className="relative bg-white overflow-hidden">
            {mode === "side" && (
              <div className="absolute inset-0 grid grid-cols-2 gap-px bg-[var(--fop-border)]">
                <Pane title={other ? `REV ${other.revision}` : "PRIOR"} url={otherUrl} accent="var(--fop-text-3)" testid="compare-pane-other" />
                <Pane title={`REV ${sheet.revision}`} url={currentUrl} accent="var(--fop-primary)" testid="compare-pane-current" />
              </div>
            )}
            {mode === "overlay" && (
              <ReviewStage
                ref={stageRef}
                url1={otherUrl}
                url2={currentUrl}
                opacity1={1 - opacity}
                opacity2={opacity}
                regions={diffOn ? grouped.all : []}
                focusedIdx={focusedIdx}
                onRegionClick={focusRegion}
                testid="compare-stage-overlay"
              />
            )}
            {mode === "blink" && (
              <ReviewStage
                ref={stageRef}
                url1={otherUrl}
                url2={currentUrl}
                opacity1={blinkShowOther ? 1 : 0}
                opacity2={blinkShowOther ? 0 : 1}
                regions={diffOn ? grouped.all : []}
                focusedIdx={focusedIdx}
                onRegionClick={focusRegion}
                testid="compare-stage-blink"
              />
            )}
            {/* Overlay-mode opacity slider — anchored bottom-centre so it
                doesn't fight the drawing for attention. */}
            {mode === "overlay" && other && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[var(--fop-surface)]/95 border border-[var(--fop-border)] rounded-md px-3 py-1 backdrop-blur">
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">REV {other.revision}</span>
                <input
                  type="range" min={0} max={1} step={0.01}
                  value={opacity}
                  onChange={(e) => setOpacity(parseFloat(e.target.value))}
                  className="w-44 accent-[var(--fop-primary)]"
                  data-testid="compare-opacity"
                />
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)]">REV {sheet.revision}</span>
              </div>
            )}
          </div>

          {/* Right-side categorized change panel */}
          <div className="border-l border-[var(--fop-border)] bg-[var(--fop-surface)] flex flex-col min-h-0">
            <div className="px-4 py-3 border-b border-[var(--fop-border)]">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
                Revisions
              </div>
              <div className="font-display text-base text-[var(--fop-text)]">
                Total Changes: <span data-testid="review-total-changes">{totalChanges}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              <ChangeGroup
                kind="added"
                label="Added"
                items={grouped.added}
                focusedIdx={focusedIdx}
                onPick={focusRegion}
              />
              <ChangeGroup
                kind="modified"
                label="Modified"
                items={grouped.modified}
                focusedIdx={focusedIdx}
                onPick={focusRegion}
              />
              <ChangeGroup
                kind="removed"
                label="Removed"
                items={grouped.removed}
                focusedIdx={focusedIdx}
                onPick={focusRegion}
              />
            </div>

            {/* Review summary + submit */}
            {showSummary && (
              <div className="border-t border-[var(--fop-border)] bg-[var(--fop-surface-2)] p-3" data-testid="review-summary-panel">
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1.5">
                  Decision (required)
                </div>
                <select
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                  className="w-full text-xs bg-[#0a0a0b] border border-[var(--fop-border)] rounded p-2 mb-2 focus:border-[var(--fop-primary)] outline-none text-[var(--fop-text)]"
                  data-testid="review-decision-select"
                >
                  <option value="carry_forward_approved">Approved — trust carried-forward field data</option>
                  <option value="requires_rework">Requires rework — revision needs a correction</option>
                  <option value="escalated_to_engineer">Escalated to engineer — beyond my authority to decide</option>
                </select>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1.5">
                  Review summary (optional)
                </div>
                <textarea
                  value={summaryText}
                  onChange={(e) => setSummaryText(e.target.value)}
                  placeholder="Operational notes — e.g. 'Curb inlet DS-15 added at STA 11+25; field coordination required with utility crew.'"
                  className="w-full h-20 text-xs bg-[#0a0a0b] border border-[var(--fop-border)] rounded p-2 focus:border-[var(--fop-primary)] outline-none resize-none text-[var(--fop-text)]"
                  data-testid="review-summary-textarea"
                  maxLength={1000}
                />
                <button
                  onClick={submitReview}
                  disabled={submitting || !other}
                  className="mt-2 w-full btn btn-primary text-xs flex items-center justify-center gap-1.5"
                  data-testid="review-submit-btn"
                >
                  <Check size={12} />
                  {submitting ? "Recording…" : "Submit review"}
                </button>
              </div>
            )}

            {/* Reviewer attribution + history */}
            <div className="border-t border-[var(--fop-border)] bg-[var(--fop-surface-2)] p-3 text-[11px] text-[var(--fop-text-2)] space-y-1.5" data-testid="review-attribution">
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                This review compares
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[var(--fop-text)]">REV {sheet.revision}</span>
                <span className="text-[var(--fop-text-3)]">vs</span>
                <span className="font-mono text-[var(--fop-text)]">REV {other?.revision ?? "?"}</span>
              </div>
              {reviews.length > 0 ? (
                <div className="mt-2 pt-2 border-t border-[var(--fop-border)] space-y-1">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">
                    Past reviews · {reviews.length}
                  </div>
                  {reviews.slice(0, 3).map((rv) => (
                    <div key={rv.id} className="text-[10px] text-[var(--fop-text-3)] flex items-center gap-2 justify-between" data-testid={`review-history-${rv.id}`}>
                      <span className="truncate">
                        {rv.reviewer_name} · {(ROLE_LABEL[rv.reviewer_role] || rv.reviewer_role).toUpperCase()}
                        {rv.decision && (
                          <span className={`ml-1.5 font-mono uppercase ${
                            rv.decision === "requires_rework" ? "text-[var(--fop-error)]"
                              : rv.decision === "escalated_to_engineer" ? "text-[var(--fop-warn)]"
                              : "text-[var(--fop-success,#10b981)]"
                          }`} data-testid={`review-decision-badge-${rv.id}`}>
                            [{rv.decision.replace(/_/g, " ")}]
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-[var(--fop-text-3)]">
                        {new Date(rv.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-[var(--fop-text-3)] italic">No reviews yet — be the first.</div>
              )}

              {/* Iter 32 — field ack roster summary for the most recent
                  review on this sheet. Shows the operational pulse: who's
                  pending, who's acknowledged, who raised a conflict. */}
              {ackSummary?.all?.total > 0 && (
                <div className="mt-2 pt-2 border-t border-[var(--fop-border)]" data-testid="review-ack-summary-wrap">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1.5">
                    Field acknowledgment
                  </div>
                  <AckSummary counts={ackSummary.all} testid="review-ack-summary" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ====================================================================== */
/* SUB-COMPONENTS                                                         */
/* ====================================================================== */


function HeaderBtn({ onClick, icon: Icon, label, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider flex items-center gap-1.5 transition-colors border ${
        active
          ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] border-[var(--fop-primary)]/50"
          : "text-[var(--fop-text-2)] border-[var(--fop-border)] hover:border-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
      }`}
    >
      <Icon size={11} />
      {label}
    </button>
  );
}


function RevisionDropdown({ label, value, options, onChange, accent, testid }) {
  const [open, setOpen] = useState(false);
  if (!options || options.length === 0) {
    return (
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] italic">
        no prior rev
      </div>
    );
  }
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text)] hover:bg-white/5"
        style={{ color: accent }}
        data-testid={testid}
      >
        <span className="text-[var(--fop-text-3)] text-[9px]">{label}</span>
        <span>REV {value?.revision ?? "?"}</span>
        <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-[160px] bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-xl py-1">
          {options.map((r) => (
            <button
              key={r.id}
              onClick={() => { onChange(r); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider flex items-center justify-between ${
                value?.id === r.id ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-2)] hover:bg-white/5"
              }`}
              data-testid={`${testid}-opt-${r.revision}`}
            >
              <span>REV {r.revision}</span>
              <span className="text-[9px] text-[var(--fop-text-3)]">
                {r.is_current ? "current" : new Date(r.revision_date || r.created_at).toLocaleDateString()}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function LegendChip({ kind, count }) {
  const palette = CLOUD_PALETTE[kind] || {};
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]" data-testid={`legend-${kind}`}>
      <span
        className="inline-block w-2.5 h-2.5 rounded-sm border"
        style={{ borderColor: palette.stroke, background: palette.fill }}
      />
      <span>{kind}</span>
      <span className="text-[var(--fop-text-3)]">·</span>
      <span className="text-[var(--fop-text)]">{count}</span>
    </div>
  );
}


function ChangeGroup({ kind, label, items, focusedIdx, onPick }) {
  const [open, setOpen] = useState(true);
  const palette = CLOUD_PALETTE[kind] || {};
  return (
    <div data-testid={`change-group-${kind}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5"
      >
        <ChevronRight
          size={12}
          className={`transition-transform text-[var(--fop-text-3)] ${open ? "rotate-90" : ""}`}
        />
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ background: palette.stroke }}
        />
        <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--fop-text-2)]">{label}</span>
        <span className="text-[10px] font-mono text-[var(--fop-text-3)]">({items.length})</span>
      </button>
      {open && (
        <div className="pl-7 pr-2 py-0.5 space-y-0.5">
          {items.length === 0 && (
            <div className="text-[10px] text-[var(--fop-text-3)] italic px-2 py-1">No items</div>
          )}
          {items.map((it) => (
            <button
              key={it.index}
              onClick={() => onPick(it)}
              className={`w-full text-left text-[11px] font-mono px-2 py-1 rounded transition-colors flex items-center justify-between ${
                focusedIdx === it.index
                  ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
                  : "text-[var(--fop-text-2)] hover:bg-white/5"
              }`}
              data-testid={`change-item-${it.index}`}
            >
              <span>
                <span className="text-[var(--fop-text-3)] mr-2">{it.index}</span>
                {label} region
              </span>
              <span className="text-[9px] text-[var(--fop-text-3)] uppercase">{Math.round(it.nw * 100)}×{Math.round(it.nh * 100)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function ModeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider flex items-center gap-1.5 transition-colors border ${
        active
          ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] border-[var(--fop-primary)]/50"
          : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] border-transparent hover:border-[var(--fop-border)]"
      }`}
    >
      <Icon size={11} />
      {label}
    </button>
  );
}


function Pane({ title, url, accent, testid }) {
  if (!url) {
    return (
      <div className="bg-[var(--fop-surface)] flex items-center justify-center text-[var(--fop-text-3)] text-sm">
        No revision selected.
      </div>
    );
  }
  return (
    <div className="bg-[var(--fop-surface)] flex flex-col overflow-hidden" data-testid={testid}>
      <div className="px-3 py-1.5 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>
        {title}
      </div>
      <div className="flex-1 overflow-auto bg-white flex items-center justify-center">
        <img src={url} alt="" className="max-w-full max-h-full object-contain" />
      </div>
    </div>
  );
}


/**
 * ReviewStage — overlays two rasters and renders revision-cloud overlays
 * on top. Scrollable; ref-able so the parent can pan to a focused region.
 */
const ReviewStage = React.forwardRef(function ReviewStage(
  { url1, url2, opacity1, opacity2, regions, focusedIdx, onRegionClick, testid },
  ref,
) {
  return (
    <div
      ref={ref}
      className="absolute inset-0 overflow-auto bg-white"
      data-testid={testid}
    >
      <div className="relative inline-block" data-content>
        {url1 && (
          <img src={url1} alt="" draggable={false}
               className="block max-w-none transition-opacity duration-150"
               style={{ opacity: opacity1 }} />
        )}
        {url1 && url2 && (
          <img src={url2} alt="" draggable={false}
               className="block max-w-none absolute inset-0 transition-opacity duration-150"
               style={{ opacity: opacity2 }} />
        )}
        {regions && regions.length > 0 && (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full"
            data-testid="compare-diff-regions"
            style={{ pointerEvents: "none" }}
          >
            {regions.map((r) => (
              <g key={r.index} style={{ pointerEvents: "auto" }}>
                <RevisionCloud
                  region={r}
                  index={r.index}
                  focused={focusedIdx === r.index}
                  onClick={() => onRegionClick && onRegionClick(r)}
                  testid={`revision-cloud-${r.index}`}
                />
              </g>
            ))}
          </svg>
        )}
      </div>
    </div>
  );
});
